import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { ExtractionRequestError } from "@/features/data-curation/api/curation";
import { ColumnFields } from "@/features/data-curation/components/ColumnFields";
import {
  useAddExtractionColumn,
  useFastFillColumns,
} from "@/features/data-curation/hooks/useCuration";
import { useCurationAgentMessage } from "@/features/data-curation/hooks/useCurationAgentMessage";
import {
  FAST_COLUMN_TYPES,
  MAX_COLUMNS,
  addColumnMessage,
  columnsPayload,
  newColumnDraft,
  toColumnDrafts,
  validateColumnDrafts,
  type ColumnDraft,
  type ColumnDraftErrors,
} from "@/features/data-curation/schemaDraft";
import type { ExtractionDataset } from "@/features/data-curation/types";
import { Plus } from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";

type AddColumnDialogProps = {
  dataset: ExtractionDataset;
  open: boolean;
  agentWorking: boolean;
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
};

const addErrorMessage = (error: unknown) => {
  if (error instanceof ExtractionRequestError) {
    if (/revision conflict/i.test(error.message)) {
      return "The dataset changed while you were working. Try again.";
    }
    if (error.status === 422) {
      return "The server did not accept this column. Check the label, type, and examples.";
    }
  }
  return error instanceof Error ? error.message : "The column could not be added.";
};

/** Adds one optional column to an approved dataset, and can ask the agent to fill it. */
export const AddColumnDialog = ({
  dataset,
  open,
  agentWorking,
  onClose,
  onCloseAutoFocus,
}: AddColumnDialogProps) => {
  const id = useId();
  const [draft, setDraft] = useState<ColumnDraft>(newColumnDraft);
  const [askAgent, setAskAgent] = useState(true);
  const [showProblems, setShowProblems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addColumn = useAddExtractionColumn(dataset.id);
  const fastFill = useFastFillColumns(dataset.id);
  // The Fast builder fills a new column of its dataset from the papers it read.
  const fast = Boolean(dataset.builder);
  const messageAgent = useCurationAgentMessage(dataset.chat_session_id);
  const hasChat = Boolean(dataset.chat_session_id);
  const full = dataset.columns.length >= MAX_COLUMNS;
  const problems = useMemo<ColumnDraftErrors>(
    () =>
      validateColumnDrafts([...toColumnDrafts(dataset.columns), draft]).errors[draft.key] ?? {},
    [dataset.columns, draft],
  );
  const shown = showProblems ? problems : {};

  const change = (patch: Partial<ColumnDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const reset = () => {
    setDraft(newColumnDraft());
    setShowProblems(false);
    setError(null);
  };
  const close = () => {
    if (addColumn.isPending) return;
    reset();
    onClose();
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowProblems(true);
    if (full || problems.label || problems.examples || problems.options) return;
    setError(null);
    const [column] = columnsPayload([draft], []);
    const fill = !fast && hasChat && askAgent && !agentWorking;
    let added: ExtractionDataset;
    try {
      added = await addColumn.mutateAsync({
        expectedRevision: dataset.revision,
        column: { ...column, required: false },
        chatNote: !fill && !fast,
      });
    } catch (addError) {
      setError(addErrorMessage(addError));
      return;
    }
    if (fill) messageAgent(addColumnMessage(dataset.id, column.label));
    const newColumn = fast
      ? added.columns.find((item) => !dataset.columns.some((old) => old.column_id === item.column_id))
      : undefined;
    if (newColumn) fastFill.mutate([newColumn.column_id]);
    toast.success(
      newColumn
        ? "Column added. Fast fills it from the papers it read."
        : fill
          ? "Column added. The agent fills it in the chat."
          : "Column added.",
    );
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <form noValidate onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>Add a column</DialogTitle>
            <DialogDescription>
              The rows and their checked cells stay. The new column is optional, and
              its cells are empty until the agent fills them.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <ColumnFields
              id={id}
              draft={draft}
              problems={shown}
              onChange={change}
              allowedTypes={fast ? FAST_COLUMN_TYPES : undefined}
            />
            {fast ? (
              <p className="text-xs text-zinc-500">
                Fast fills the column from the passages of the papers that it read.
              </p>
            ) : hasChat ? (
              <div>
                <label className="flex items-start gap-2 text-sm text-zinc-700">
                  <input
                    type="checkbox"
                    checked={askAgent && !agentWorking}
                    disabled={agentWorking}
                    onChange={(event) => setAskAgent(event.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-none border-zinc-400 text-zinc-900 focus:ring-2 focus:ring-zinc-900 disabled:opacity-50"
                  />
                  Ask the agent in the chat to fill it for the rows in the grid
                </label>
                {agentWorking && (
                  <p className="mt-1 text-xs text-zinc-500" aria-live="polite">
                    The agent is working. When it stops, ask it in the chat to fill
                    the column.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-zinc-500">
                To fill the column, ask your agent after you add it.
              </p>
            )}
          </div>
          {full && (
            <p className="mt-3 text-sm text-red-700" role="alert">
              A dataset has at most {MAX_COLUMNS} columns.
            </p>
          )}
          {error && (
            <p
              className="mt-3 rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </p>
          )}
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={close} disabled={addColumn.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={addColumn.isPending}
              disabled={full}
              aria-label={addColumn.isPending ? "Adding column" : undefined}
            >
              <Plus className="h-4 w-4" aria-hidden />
              Add column
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
